import React from 'react';
import type { ReviewResultBreakdown } from '../../types/dashboard.types';

interface ReviewBreakdownCardProps {
  breakdown: ReviewResultBreakdown;
  accuracy: number;
}

export const ReviewBreakdownCard: React.FC<ReviewBreakdownCardProps> = ({
  breakdown,
  accuracy,
}) => {
  const total = breakdown.total;

  const getPercent = (count: number) => {
    if (total === 0) return 0;
    return Math.round((count / total) * 100);
  };

  return (
    <div className="rounded-2xl p-6 border border-white/10 bg-surface-800/80 backdrop-blur-sm space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-white tracking-tight">Review Ratings Distribution</h3>
          <p className="text-xs text-slate-400 mt-0.5">
            Recall difficulty breakdown based on spaced repetition results
          </p>
        </div>
        <div className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-bold text-sm">
          <span>{accuracy}%</span>
          <span className="text-xs font-normal text-emerald-300">Accuracy</span>
        </div>
      </div>

      {/* Progress Bar Track */}
      <div className="w-full h-3 rounded-full bg-surface-900 overflow-hidden flex">
        {total > 0 ? (
          <>
            <div
              style={{ width: `${getPercent(breakdown.again)}%` }}
              title={`Again: ${breakdown.again} (${getPercent(breakdown.again)}%)`}
              className="bg-rose-500 h-full transition-all duration-500"
            />
            <div
              style={{ width: `${getPercent(breakdown.hard)}%` }}
              title={`Hard: ${breakdown.hard} (${getPercent(breakdown.hard)}%)`}
              className="bg-amber-500 h-full transition-all duration-500"
            />
            <div
              style={{ width: `${getPercent(breakdown.good)}%` }}
              title={`Good: ${breakdown.good} (${getPercent(breakdown.good)}%)`}
              className="bg-emerald-500 h-full transition-all duration-500"
            />
            <div
              style={{ width: `${getPercent(breakdown.easy)}%` }}
              title={`Easy: ${breakdown.easy} (${getPercent(breakdown.easy)}%)`}
              className="bg-sky-500 h-full transition-all duration-500"
            />
          </>
        ) : (
          <div className="w-full bg-white/5 h-full" />
        )}
      </div>

      {/* Breakdown Stat Pills */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1">
        {/* Again */}
        <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 flex flex-col justify-between">
          <span className="text-xs font-semibold text-rose-300">Again (&lt;10m)</span>
          <div className="mt-2 flex items-baseline justify-between">
            <span className="text-xl font-bold text-white">{breakdown.again}</span>
            <span className="text-xs text-rose-400 font-mono font-semibold">
              {getPercent(breakdown.again)}%
            </span>
          </div>
        </div>

        {/* Hard */}
        <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 flex flex-col justify-between">
          <span className="text-xs font-semibold text-amber-300">Hard (+1d)</span>
          <div className="mt-2 flex items-baseline justify-between">
            <span className="text-xl font-bold text-white">{breakdown.hard}</span>
            <span className="text-xs text-amber-400 font-mono font-semibold">
              {getPercent(breakdown.hard)}%
            </span>
          </div>
        </div>

        {/* Good */}
        <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex flex-col justify-between">
          <span className="text-xs font-semibold text-emerald-300">Good (+3d)</span>
          <div className="mt-2 flex items-baseline justify-between">
            <span className="text-xl font-bold text-white">{breakdown.good}</span>
            <span className="text-xs text-emerald-400 font-mono font-semibold">
              {getPercent(breakdown.good)}%
            </span>
          </div>
        </div>

        {/* Easy */}
        <div className="p-3 rounded-xl bg-sky-500/10 border border-sky-500/20 flex flex-col justify-between">
          <span className="text-xs font-semibold text-sky-300">Easy (+5d)</span>
          <div className="mt-2 flex items-baseline justify-between">
            <span className="text-xl font-bold text-white">{breakdown.easy}</span>
            <span className="text-xs text-sky-400 font-mono font-semibold">
              {getPercent(breakdown.easy)}%
            </span>
          </div>
        </div>
      </div>

      <p className="text-[11px] text-slate-500 italic">
        * Accuracy is calculated as (Good + Easy) / Total reviews ({breakdown.good + breakdown.easy} / {total || 1}).
      </p>
    </div>
  );
};
